/**
 * Global period context — ONE date-range control for the whole app (research:
 * users expect a global time filter at the top, with presets + the resolved
 * dates shown). Pages thread `query` into their request params and `cacheKey`
 * into their react-query keys; the server resolves the same window (keyword OR a
 * custom from/to range) so card and drill windows reconcile exactly.
 *
 * Custom calendar range (#3, 2026-07-07): key === "custom" carries {from, to}
 * (ISO YYYY-MM-DD, `to` EXCLUSIVE — half-open, matching the server + DrillExplorer
 * CustomRange). Standard pills clear the custom range. Both persist per browser.
 */

import { createContext, useContext, useEffect, useState } from "react";

export type PeriodKey = "30d" | "90d" | "365d" | "ytd" | "all" | "custom";
/** The fixed windows shown as pills (custom is a separate calendar affordance). */
export type StandardKey = Exclude<PeriodKey, "custom">;

export const PERIODS: Array<{ value: StandardKey; label: string; short: string }> = [
  { value: "30d", label: "Last 30 days", short: "30D" },
  { value: "90d", label: "Last 90 days", short: "90D" },
  { value: "365d", label: "Last 365 days", short: "1Y" },
  { value: "ytd", label: "Year to date", short: "YTD" },
  { value: "all", label: "All time", short: "All" },
];

const LS_KEY = "dd-period";

interface CustomRange { from: string; to: string }

interface PeriodCtx {
  key: PeriodKey;
  label: string;
  /** e.g. "May 11 – Jun 10" — display-only mirror of the server's UTC window. */
  rangeHint: string;
  /** Custom range (only when key === "custom"). `to` is EXCLUSIVE. */
  from?: string;
  to?: string;
  /** Pick a fixed window — clears any custom range. */
  setKey: (k: StandardKey) => void;
  /** Pick a custom range (ISO YYYY-MM-DD, `to` EXCLUSIVE = +1 day past the last day). */
  setCustom: (from: string, to: string) => void;
  /** Request params: {period} for a fixed window, {from,to} for a custom range. */
  query: { period?: string; from?: string; to?: string };
  /** Stable token for react-query keys — distinguishes different custom ranges
   *  (period.key is the constant "custom" for all of them, which would collide). */
  cacheKey: string;
  /** Days in the window, or null for "all" (100-year lookback). */
  windowDays: number | null;
  /** The window to hand a drill so it inherits this range: {key} for a fixed
   *  window, {from,to} for a custom one (a drill sends from/to when there's no
   *  key — DrillExplorer). Pass as a DrillRequest's `period`. */
  drillWindow: { key?: PeriodKey; from?: string; to?: string; label: string };
}

const Ctx = createContext<PeriodCtx | null>(null);

function isStandardKey(v: string | null): v is StandardKey {
  return v === "30d" || v === "90d" || v === "365d" || v === "ytd" || v === "all";
}

const fmtDay = (d: Date): string => d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const fmtDayWithYear = (d: Date): string => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/** Mirrors resolvePeriod's UTC math for display (rolling window ending now). */
export function standardRangeHint(key: StandardKey, now = new Date()): string {
  if (key === "all") return "all history";
  const end = now;
  if (key === "ytd") {
    const start = new Date(Date.UTC(end.getUTCFullYear(), 0, 1));
    return `${fmtDay(start)} – ${fmtDay(end)}`;
  }
  const days = standardWindowDays(key, end)!;
  const start = new Date(end.getTime() - days * 86_400_000);
  // A rolling year commonly starts and ends on the same month/day. Without
  // both years, Aug 11, 2025–Aug 11, 2026 looked like a one-day window.
  if (key === "365d" && start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${fmtDayWithYear(start)} – ${fmtDayWithYear(end)}`;
  }
  return `${fmtDay(start)} – ${fmtDay(end)}`;
}

function standardWindowDays(key: StandardKey, now: Date): number | null {
  if (key === "all") return null;
  if (key === "30d") return 30;
  if (key === "90d") return 90;
  if (key === "365d") return 365;
  return Math.max(1, Math.round((now.getTime() - Date.UTC(now.getUTCFullYear(), 0, 1)) / 86_400_000)); // ytd
}

/** Window days for a keyword period (public helper for callers that only carry a
 *  key, e.g. widget contexts). 'custom' returns null (no from/to available here). */
export function windowDaysForKey(key: PeriodKey): number | null {
  return isStandardKey(key) ? standardWindowDays(key, new Date()) : null;
}

/** "2026-01-01".."2026-02-01" (to EXCLUSIVE) → "Jan 1 – Jan 31, 2026". */
function customLabel(from: string, to: string): string {
  const start = new Date(from);
  const endIncl = new Date(new Date(to).getTime() - 86_400_000); // show the inclusive last day
  const y = endIncl.getUTCFullYear();
  return `${fmtDay(start)} – ${fmtDay(endIncl)}, ${y}`;
}

function readCustom(storageKey: string): CustomRange | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<CustomRange>;
    if (typeof p.from !== "string" || typeof p.to !== "string") return null;
    if (Number.isNaN(new Date(p.from).getTime()) || Number.isNaN(new Date(p.to).getTime())) return null;
    if (new Date(p.from).getTime() >= new Date(p.to).getTime()) return null;
    return { from: p.from, to: p.to };
  } catch {
    return null;
  }
}

export function PeriodProvider({ children, defaultKey = "30d", storageKey = LS_KEY }: { children: React.ReactNode; defaultKey?: StandardKey; storageKey?: string }): React.ReactElement {
  const customStorageKey = `${storageKey}-custom`;
  const [key, setKeyState] = useState<PeriodKey>(() => {
    if (typeof localStorage === "undefined") return defaultKey;
    const saved = localStorage.getItem(storageKey);
    if (saved === "custom" && readCustom(customStorageKey)) return "custom";
    return isStandardKey(saved) ? saved : defaultKey;
  });
  const [custom, setCustomState] = useState<CustomRange | null>(() => readCustom(customStorageKey));

  useEffect(() => {
    localStorage.setItem(storageKey, key);
  }, [key, storageKey]);
  useEffect(() => {
    if (custom) localStorage.setItem(customStorageKey, JSON.stringify(custom));
  }, [custom, customStorageKey]);

  const setKey = (k: StandardKey): void => { setCustomState(null); setKeyState(k); };
  const setCustom = (from: string, to: string): void => { setCustomState({ from, to }); setKeyState("custom"); };

  const isCustom = key === "custom" && custom !== null;
  const now = new Date();
  const value: PeriodCtx = isCustom
    ? {
        key: "custom",
        label: customLabel(custom!.from, custom!.to),
        rangeHint: customLabel(custom!.from, custom!.to),
        from: custom!.from,
        to: custom!.to,
        setKey,
        setCustom,
        query: { from: custom!.from, to: custom!.to },
        cacheKey: `custom:${custom!.from}:${custom!.to}`,
        windowDays: Math.max(1, Math.round((new Date(custom!.to).getTime() - new Date(custom!.from).getTime()) / 86_400_000)),
        drillWindow: { from: custom!.from, to: custom!.to, label: customLabel(custom!.from, custom!.to) },
      }
    : (() => {
        const k = (isStandardKey(key) ? key : defaultKey) as StandardKey;
        const meta = PERIODS.find((p) => p.value === k) ?? PERIODS[0]!;
        return {
          key: k,
          label: meta.label,
          rangeHint: standardRangeHint(k),
          setKey,
          setCustom,
          query: { period: k },
          cacheKey: k,
          windowDays: standardWindowDays(k, now),
          drillWindow: { key: k, label: meta.label },
        };
      })();

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Safe outside a provider (returns 30d defaults) so cards never crash. */
export function usePeriod(): PeriodCtx {
  return (
    useContext(Ctx) ?? {
      key: "30d",
      label: "Last 30 days",
      rangeHint: standardRangeHint("30d"),
      setKey: () => {},
      setCustom: () => {},
      query: { period: "30d" },
      cacheKey: "30d",
      windowDays: 30,
      drillWindow: { key: "30d", label: "Last 30 days" },
    }
  );
}
